import React, { FC, useContext, useEffect, useRef, useState } from 'react'
import { Popup } from '../../catalog/Popup'
import { Swap, ValidSwap } from '../../../logic/swaps/Swap'
import styled from 'styled-components'
import SwapButton from '../SwapButton'
import { ArrowDownFixed } from '../../catalog/ArrowDownFixed'
import { EmptyLine } from '../../catalog/EmptyLine'
import { coinImage } from '../../../assets/coins/coins'
import CurrencyLogo from '../../CurrencyLogo'
import { fromWei } from '../../../utils/static-web3'
import SwapQuotation from '../SwapQuotation'
import { precise } from '../../../logic/financials/precise'
import { SwapStyle, swaapRed0 } from '../../Themes'
import { ViewState } from '../../pools/deposit/DepositState'
import { Wei } from '../../../utils/types'
import { TokenApi } from '../../../api/web3/token-api'
import { Web3Context } from '../../../hooks/web3'
import { useReloader } from '../../../hooks/use-reload'
import { ErrorMessage } from '../../catalog/ErrorMessage'
import { extractErrorMessage } from '../../../logic/errors/extract-error-message'
import { SimpleGrid } from '../../catalog/SimpleGrid'
import { SwapState } from '../SwapState'
import { roundSlippageForPercentDisplay, slippageIsRisky } from '../Settings'
import { showBool } from '../../catalog/show-bool'

interface Props {
  swap: Swap & ValidSwap
  state: ViewState
  onExit: () => void

  onSwap: VoidFunction
}

export const PreSwapPopup: FC<Props> = (props: Props) => {
  const { onSwap, onExit, swap, state } = props
  const { ACCEPTANCE_DONE, SUBMITTING, PROCESSING } = SwapState
  const { tokenIn, tokenOut } = swap
  const title = 'Confirm swap'
  const { web3, account, appChainID, proxy } = useContext(Web3Context)
  const { nonce } = useReloader()
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  const [allowance, setAllowance] = useState<Wei | undefined>(undefined)
  const [allowed, setAllowed] = useState<boolean | undefined>(undefined)
  const [allowing, setAllowing] = useState<boolean>(false)

  async function allow() {
    setErrorMessage(undefined)
    if (web3 && account) {
      const tokenApi = new TokenApi(web3, account, tokenIn)

      try {
        setAllowing(true)
        const allowance = await tokenApi.allow(proxy)
        setAllowance(allowance)
        setAllowed(true)
        setAllowing(false)
      } catch (error) {
        setAllowance(undefined)
        setErrorMessage(extractErrorMessage(error))
        setAllowed(false)
        setAllowing(false)
      }
    } else {
      setErrorMessage('You are not connected')
    }
  }

  useEffect(() => {
    if (web3 && account) {
      const tokenApi = new TokenApi(web3, account, tokenIn)
      tokenApi
        .allowance(proxy)
        .then(allowance => {
          setAllowance(allowance)
          setAllowed(Number(allowance) > Number(swap.inAmountWei))
        })
        .catch((e: any) => {
          console.error(e)
          setErrorMessage('Unable to estimate token allowance')
        })
    }
  }, [])

  // TODO:use specific functions
  const inAmount = fromWei(swap.inAmountWei, swap.tokenIn.decimals).toPrecision(
    7
  )
  const outAmount = fromWei(
    swap.expectedOutAmountWei,
    swap.tokenOut.decimals
  ).toPrecision(7)
  const minimalAmountWei = swap.calculateMinAmountWei()
  const ref = useRef()
  return (
    <PopupWrapper>
      <Popup outsideRef={ref} onExit={onExit} title={title}>
        <PVSwapLine className={'pt20'}>
          <div className={'align-center'}>
            <CurrencyLogo src={coinImage(tokenIn.symbol)!} />
            <PVTokenSymbol>{tokenIn.symbol}</PVTokenSymbol>
          </div>
          <PVSwapQuantity>{inAmount}</PVSwapQuantity>
        </PVSwapLine>
        <ArrowDownFixed />
        <PVSwapLine className={'fs20'}>
          <div className={'align-center'}>
            <CurrencyLogo src={coinImage(tokenOut.symbol)!} />
            <PVTokenSymbol>{tokenOut.symbol}</PVTokenSymbol>
          </div>
          <PVSwapQuantity>{outAmount}</PVSwapQuantity>
        </PVSwapLine>
        <SwapQuotation
          inCurrency={tokenIn}
          outCurrency={tokenOut}
          inQuantity={inAmount}
          outAmountWei={swap.expectedOutAmountWei}
        />
        <PVProductionPanel className={'pt20 light'}>
          <PVProductionLine main={true} className={'justify-between'}>
            <div>Expected output</div>
            <div className={'numeric'}>
              {outAmount} {tokenOut.symbol}
            </div>
          </PVProductionLine>
          <PVProductionLine main={false} className={'justify-between'}>
            <div className={'pr20'}>
              Minimum output <br />
              <div
                className={'small-text'}
                style={{
                  flex: 'display',
                  flexDirection: 'column',
                }}
              >
                {slippageIsRisky(swap.slippage) ? (
                  <span style={{ color: swaapRed0, opacity: '0.9' }}>
                    Slippage tolerance (
                    <span className={'numeric'}>
                      {roundSlippageForPercentDisplay(swap.slippage)}%
                    </span>
                    ) is low, transaction may fail.
                  </span>
                ) : (
                  <span style={{ opacity: '0.7' }}>
                    (Slippage tolerance :{' '}
                    <span className={'numeric'}>{swap.slippage * 100}%</span>)
                  </span>
                )}
              </div>
            </div>
            <div className={'numeric'} style={{ alignSelf: 'start' }}>
              {precise(fromWei(minimalAmountWei, tokenOut.decimals))}{' '}
              {tokenOut.symbol}
            </div>
          </PVProductionLine>
        </PVProductionPanel>
        <PVProductionInformation className={'pt20 light'}>
          <div style={{ textAlign: 'left' }}>
            Expected output is an estimation. You will receive at least{' '}
            <span className={'numeric'}>
              {precise(fromWei(minimalAmountWei, tokenOut.decimals))}
            </span>{' '}
            {tokenOut.symbol} or the transaction will be cancelled.
          </div>
        </PVProductionInformation>

        <EmptyLine />

        {/*
        <div>Allowance : {allowance}</div>
        <div>Asked: {swap.inAmountWei}</div>
        <div>Allowed : {JSON.stringify(allowed)}</div>
        <div>Enough : {isEnough() ? 'yep' : 'nope'}</div>
      */}

        <SimpleGrid>
          {errorMessage && (
            <div className={'mt20'}>
              <ErrorMessage>{errorMessage}</ErrorMessage>
            </div>
          )}
          {allowed === false && (
            <SwapButton
              handleOnClick={() => allow()}
              message={`Allow Swaap to use your ${tokenIn.symbol}`}
              isReadyToSwap={!allowing}
              waiting={allowing}
              icon={<CurrencyLogo src={coinImage(tokenIn.symbol)!} />}
              isSmall={true}
            />
          )}
          <SwapButton
            handleOnClick={onSwap}
            message={title}
            isReadyToSwap={allowed === true && state !== SUBMITTING}
          />
        </SimpleGrid>
      </Popup>
    </PopupWrapper>
  )
}

export const PVSwapLine = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 2rem;
  min-width: 300px;
`

export const PVTokenSymbol = styled.span`
  text-align: left;
  font-size: 1.75rem;
  font-family: Montserrat-Light;
`
export const PVSwapQuantity = styled.span`
  text-align: left;
  font-family: Montserrat-Medium, Montserrat;
  font-size: 2.25rem;
`

export const PVProductionPanel = styled.div`
  text-align: left;
`
export const PVProductionLine = styled.div<{ main: boolean }>`
  text-align: left;
`
export const PVProductionInformation = styled.div`
  max-width: 400px;
  font-size: ${SwapStyle.smallText};
  color: ${props => props.theme.shadowText};
  font-family: Montserrat-Light;
`

// TODO: include this in the Popup component
const PopupWrapper = styled.div`
  font-size: 1.6rem;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  justify-content: center;
`
